<template>
    <div class="patent-view">
        <div
            v-if="!isOverFreeViewLimit"
            :class="['patent-app', 'draggable-panel-bounce', { 'show-patent-list': isShowPatentList, 'no-patent-list': isFree, 'family-search-mode': isFamilySearch }]"
        >
            <FamilySearchHeader v-if="isFamilySearch" />
            <!--main-body-->
            <div class="patent-app__main">
                <!--header-->
                <div v-if="!isInFamilyOverview" class="patent-app__header">
                    <!--title-->
                    <InfoTitle :patent="patent" :showPlaceholder="routeLoading" class="patent-app__info-title" />
                    <!--function-bar-->
                    <FunctionBar
                        id="toolbar"
                        :key="patentId"
                        :patentId="patentId"
                        :workspaceId="workspaceId"
                        :folderId="folderId"
                        :pn="pn"
                        :viewParams="viewParams"
                        class="patent-app__function-bar"
                    />
                    <keep-alive>
                        <BioPanel
                            v-if="bioPanelVisible"
                            :patentId="patentId"
                            :pn="pn"
                            @close="closeBioPanel"
                        />
                    </keep-alive>
                </div>
                <div v-else class="patent-app__header-placeholder" />
                <!--router-view-->
                <div :class="{ [CLASS_NAME_APP_CONTENT]: true, ['dual-view']: isDualView, ['single-view']: !isDualView }" flex>
                    <!--左侧导航-->
                    <SideNav
                        :key="patentId"
                        v-addIframe
                        :patentId="patentId"
                        :closeWhenClickoutside="isDualView"
                    />
                    <!--单视图入口-->
                    <SingleView
                        v-if="!isDualView"
                        :key="`${patentId}-single`"
                        :viewParams="viewParams"
                        :selfName="$route.meta.pluginName"
                        :class="classNames"
                        class="pa-grow"
                    />
                    <!--双视图入口-->
                    <main
                        v-if="isDualView"
                        :class="{ ...classNames, 'flex-full-stretch': true, 'dual-view__container': true, 'dual-view__container--has-setting': !isFree && !isSpecialIp }"
                        flex="-"
                    >
                        <ResizeObserver @notify="dualResizeHandler" />
                        <router-view
                            :key="`${patentId}-left`"
                            ref="viewLeft"
                            :style="{ width: `${dualDrag_leftSideWidth * 100}%` }"
                            :neighborName="viewParams.neighborhoodsNames.right"
                            :viewParams="viewParams"
                            :selfName="viewParams.neighborhoodsNames.left"
                            class="view-left"
                            name="left"
                            neighborSide="right"
                            side="left"
                        />
                        <router-view
                            :key="`${patentId}-right`"
                            ref="viewRight"
                            :style="{ width: `${(1 - dualDrag_leftSideWidth) * 100}%` }"
                            :neighborName="viewParams.neighborhoodsNames.left"
                            :viewParams="viewParams"
                            :selfName="viewParams.neighborhoodsNames.right"
                            class="view-right"
                            name="right"
                            neighborSide="left"
                            side="right"
                        />
                        <!--双视图size 拖拽-->
                        <DualViewDragLine
                            v-if="!isFree"
                            ref="dualViewDragLine"
                            v-model="dualDrag_leftSideWidth"
                            :isShowPatentList="isShowPatentList"
                            :isFree="isFree"
                            dragBaseSelector=".dual-view__container"
                        />
                        <!--双视图设置按钮-->
                        <DualViewSettingTrigger v-if="!isFree && !isSpecialIp" class="dual-view__setting-button" />
                    </main>
                    <!--right-bar-->
                    <SideFunctionBar
                        v-if="!isFree"
                        :isCollapsed="!isShowPatentList"
                        :patentId="patentId"
                        :workspaceId="workspaceId"
                        :folderId="folderId"
                        :pn="pn"
                        :viewParams="viewParams"
                        class="patent-app__right-column"
                    />
                </div>
            </div>
            <!--dialog容器-->
            <div id="float-pannel-mount-point" />
            <!--深度视图入口-->
            <DeepViewDrawer
                v-if="DeepView_Lab_Mixin_isDeepViewLabOn"
                :currentDeepViewData="DeepView_Lab_Mixin_currentDeepViewData"
                source="PATENT_VIEW"
                @close="DeepView_Lab_Mixin_currentDeepViewData = undefined"
            />
        </div>
        <!--无权限页面-->
        <PermissionTips v-else />
        <!--Toast-->
        <snap-alert
            :type="alertType"
            :visible="alertVisible"
            :message="alertMessage"
            :onClose="alertOnClose"
            :autoClose="true"
        />
        <!--多窗口标识-->
        <MultiMonitorTag v-if="!isFree" />
        <AutoDialogMountPoint :service="ptDialogAutoMounterManager" />
        <PatsnapCopilot
            class="patent-view-copilot"
            :url="url"
            @keyup.native.stop
        />
        <!--化学微前端入口-->
        <ChemicalEntry />
    </div>
</template>

<script>
import '@patsnap-biz/design-tokens/scss/_flex.scss';
import { AlertMixin } from 'library-@/mixins';
import SnapAlert from 'library-@/components/snap-ui/alert-v2';
import { mapActions, mapGetters } from 'vuex';
import _ from 'lodash';
import { installHighlightStyle, CLASS_NAME_CHEMICAL_HIGHLIGHT_SCROLL_DOM } from '@patsnap-utils/highlight';
import { writePatentHistroyApi } from 'library-@/apis/core_basic_api/history';
import PermissionTips from '@/components/no-permission/PermissionTips';
import { bindHotKeys, unBindHotKeys } from '../../utils/hotKeys';
import { mark } from '../../lib/api';
import { NAMESPACE_PATENT_VIEW } from '../../constants';
import defer from '../../utils/defer';
import InfoTitle from '../../components/InfoTitle';
import SideFunctionBar from '../side-function-bar/SideFunctionBar';
import BioPanel from '@/components/bio-highlight/bio-panel/BioPanel';
import SingleView from '../entry-view/SingleView';
import SideNav from '../side-nav/SideNav';
import { getIsFreeView } from 'library-@/utils/patent-view-free-judgment-util';
import addIframe from '../../directives/addIframe';
import DualViewDragLine from '../../components/dual-view-setting/DualViewDragLine';
import DualViewSettingTrigger from '../../components/dual-view-setting/DualViewSettingTrigger';
import { ResizeObserver } from 'vue-resize';
import {
    CLASS_NAME_APP_CONTENT,
    CLASS_NAME_SHOW_SEQUENCE,
    CLASS_NAME_HIDE_SEQUENCE,
} from '@/constants';
import bioHlbtnVisibleMixin from '@/mixins/bio-hlbtn-visible';
import { getRouteNameList } from '../../router';
import { replaceWhiteLabelTitle } from 'library-@/utils/replaceWhiteLabelTitle';
import { setGlobalRvTrackAttr } from '@/utils/rvTrack';
import DrawerDeepViewMixin from '../../components/patsnap-lab/deep-view/drawerDeepViewMixin';
import MultiMonitorTag from '../../components/multi-monitors/MultiMonitorTag';
import ptDialogAutoMounterManager, { DialogAutoMounterManager } from '../../services/PtDialogAutoMounterManager';
import { useFamilySearch } from '@/composables/use-family-search';
import { useFetchTranslateConfig } from '@/composables/use-fetch-translate-config';
import FamilySearchHeader from '@/components/family-search-header/FamilySearchHeader.vue';
import PatsnapCopilot from '@analytics/patsnap-copilot/src/components/PatsnapCopilot.vue';
import { fiveMinutesCacheKey } from '@analytics/shared/utils/five-minutes-cache-key';
import { useRemoveZendesk } from '@/composables/use-remove-zendesk';
import { usePatentsList } from '@/views/side-function-bar/patents-list/composables/use-patents-list';
import { useFreeViewLimit } from '@/components/no-permission/composables/use-free-view-limit';
import { usePatentBasic } from '@/composables/use-patent-basic';
import ChemicalEntry from '@/components/chemical/ChemicalEntry.vue';
import { getPatentShareSignApi } from 'library-@/apis/core_basic_api/view';

export default {
    name: 'ViewRoot',
    components: {
        PatsnapCopilot,
        AutoDialogMountPoint: DialogAutoMounterManager.MountPoint,
        SingleView,
        FunctionBar: () => import(/* webpackChunkName: "FunctionBar" */'../function-bar'),
        SnapAlert,
        PermissionTips,
        InfoTitle,
        SideFunctionBar,
        SideNav,
        DualViewDragLine,
        DualViewSettingTrigger,
        ResizeObserver,
        MultiMonitorTag,
        BioPanel,
        FamilySearchHeader,
        ChemicalEntry,
    },
    directives: {
        addIframe,
    },
    mixins: [
        AlertMixin,
        bioHlbtnVisibleMixin,
        DrawerDeepViewMixin,
    ],
    props: {
        viewParams: {
            type: Object,
            required: true,
        },
    },
    setup() {
        useRemoveZendesk();
        const {
            isFamilySearch,
            isInFamilyOverview,
        } = useFamilySearch();

        const {
            fetchTranslateConfig,
        } = useFetchTranslateConfig({ side: 'both' });

        const url = `${process.env.VUE_APP_MF_EUREKA_RESULT_COPILOT}?t=${fiveMinutesCacheKey()}`;

        const {
            isShowPatentList,
            setPatentListVisible,
        } = usePatentsList();

        const {
            isOverFreeViewLimit,
            setIsOverFreeView,
        } = useFreeViewLimit();

        const {
            getPatentBase,
        } = usePatentBasic();

        return {
            isFamilySearch,
            isInFamilyOverview,
            fetchTranslateConfig,
            url,
            isShowPatentList,
            setPatentListVisible,
            isOverFreeViewLimit,
            setIsOverFreeView,
            getPatentBase,
        };
    },
    data() {
        return {
            ptDialogAutoMounterManager,
            patent: {},
            routeLoading: true,
            isFree: getIsFreeView(),
            isSpecialIp: _.get(window, 'zPREFACE.globalConfig.userInfo.isSpecialIp'),
            dualDrag_leftSideWidth: 0.5,
        };
    },
    computed: {
        ...mapGetters({
            bioHighlightEnable: 'bio/highlightEnable',
            bioPanelVisible: 'bio/bioPanelVisible',
        }),
        isDualView() {
            return !this.viewParams.isSingleView;
        },
        patentId() {
            return this.viewParams.patentId;
        },
        workspaceId() {
            return this.viewParams.workspace_id;
        },
        folderId() {
            return this.viewParams.folder_id;
        },
        showList() {
            try {
                return window.location.search.split('$')[0].indexOf('page=') > -1;
            } catch (error) {
                return false;
            }
        },
        pn() {
            return _.get(this.patent, 'PN', '');
        },
        hideSequenceHighlight() {
            return !this.bioHighlightEnable;
        },
        classNames() {
            return {
                [CLASS_NAME_CHEMICAL_HIGHLIGHT_SCROLL_DOM]: true,
                [CLASS_NAME_HIDE_SEQUENCE]: this.hideSequenceHighlight,
                [CLASS_NAME_SHOW_SEQUENCE]: !this.hideSequenceHighlight,
            };
        },
    },
    watch: {
        patentId: {
            handler(newId) {
                this.getPatentBasicInfo(newId)
                    .then(patent => {
                        this.updateDocumentTitle(patent);
                    });
                // FIXME: what this ?
                this.setIsOverFreeView(false);
                if (!this.isFree) {
                    if (!this.isSpecialIp) {
                        mark(newId);
                    }
                    this.writeHistroy();
                    this.setSignatureUrl();
                }
                if (this.$route.name === 'subDual') window.rvTrack('View Patent Dual View');
            },
            immediate: true,
        },
        isShowPatentList(val) {
            this.$eventHub.$emit('pageResize', val);
        },
        '$route': {
            handler: function() {
                _.set(window, 'zPREFACE.trackData.source', getRouteNameList(this.$route));
            },
            immediate: true,
        },
        isDualView: {
            handler: function(val) {
                this.setPatentListVisible(!val);
            },
            immediate: true,
        },
    },
    created() {
        installHighlightStyle(NAMESPACE_PATENT_VIEW);
        this.CLASS_NAME_APP_CONTENT = CLASS_NAME_APP_CONTENT;
    },
    mounted() {
        // 绑定hotKey功能
        bindHotKeys(['patentList', 'navBar', 'overviewMenu'], (event, handler) => {
            window.rvTrack('HotKey', { key: handler.key });
        });
        this.fetchTranslateConfig();
        defer(1000);
    },
    beforeDestroy() {
        unBindHotKeys(['patentList', 'navBar', 'overviewMenu']);
        window.onfocus = null;
    },
    methods: {
        ...mapActions([
            'bio/setBioPanelVisible',
        ]),
        updateDocumentTitle(patent) {
            if (this.isFamilySearch) return;
            const pn = _.get(patent, 'PN', '');
            let title;
            if (pn !== '') {
                const name = _.get(patent, 'Origin_TITLE', this.$t('no-title'));
                const removeHtmlTag = name.replace(/<[^>]+>/g, '');
                title = `${pn} ${removeHtmlTag} - ${this.$t('browserTitle')}`;
            } else {
                title = this.$t('browserTitle');
            }
            const isWhiteLabel = _.get(window, 'zPREFACE.pageConfig.whiteLabel', false);
            title = replaceWhiteLabelTitle({
                title,
                isWhiteLabel: isWhiteLabel,
            });
            document.title = title;
        },
        alertOnClose() {
            this.alertVisible = false;
        },
        async getPatentBasicInfo(patentId) {
            this.routeLoading = true;
            const patent = await this.getPatentBase(patentId);
            this.patent = patent;
            this.routeLoading = false;
            // 提供埋点使用的全局变量
            setGlobalRvTrackAttr(patent);
            return patent;
        },
        writeHistroy() {
            const type = _.get(this.viewParams, '_type', 'PATENT')
                .toUpperCase();
            const params = {
                history_type: 'view',
                history_source: type,
                history_params: {},
            };
            const relatedId = _.get(this.viewParams, 'related_id', '');
            if (type === 'CITED' && relatedId === this.patentId) {
                params.history_params.related_id = relatedId;
            } else if (type === 'FAMILY') {
                params.history_params.related_id = relatedId;
                params.history_params.type = type === 'FAMILY' ? _.get(this.viewParams, '_familyType', '') : '';
            } else {
                params.history_source = 'PATENT';
                params.history_params.patent_id = this.patentId;
            }
            writePatentHistroyApi(params);
        },
        // 双视图拖拽逻辑
        dualResizeHandler() {
            if (this.isFree || !this.isDualView) return;
            const checkDualSize = _.get(this.$refs, 'dualViewDragLine.checkDualSize');
            checkDualSize && checkDualSize();
        },
        closeBioPanel() {
            this['bio/setBioPanelVisible'](false);
        },

        async setSignatureUrl() {
            const { signature, from: shareFrom, date, expire, shareId, version } = await getPatentShareSignApi(this.patentId);
            const signQuery = {
                ...this.$route.query,
                signature: decodeURIComponent(signature),
                shareFrom,
                date,
                expire,
                shareId,
                version,
            };
            this.$router.replace({ query: signQuery });
        },
    },
};
</script>

<style lang="scss">

.patsnap-copilot.patent-view-copilot {
    z-index: 10000;
}

.patent-view {
    overflow: hidden;
}

#header-wrapper {
    position: static; // TOC-25099 不知道为啥，玄学先改布局
}

.patent-app {
    //margin-top: 64px;
    height: calc(100vh - 65px);
    position: relative;

    .patent-app__main {
        height: 100%;
        overflow: hidden;

        .patent-app__header {
            height: $headerHeight;
            position: relative;
            z-index: 99;
            padding: 0 8px;
            box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.06);
            border-bottom: 1px solid #DFE1E6;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background-color: #fff;

            .patent-app__info-title {
                width: 800px;
                min-width: 0;
            }

            .patent-app__function-bar {
                flex-shrink: 0;
                font-size: 14px;
            }
        }

        .patent-app__header-placeholder {
            height: $headerHeight;
            position: relative;
            z-index: -1;
        }

        .patent-app__content {
            height: calc(100% - #{$headerHeight});
            //padding-top: 10px;
            padding-right: $rightBarCollapsedWidth;
            position: relative;

            .patent-app__right-column {
                box-shadow: -1px 0 2px 0 rgba(0, 0, 0, 0.06);
                width: $rightBarCollapsedWidth;
                height: 100%;
                border-left: 1px solid #DFE1E6;
                position: absolute;
                right: 0;
                top: 0;
            }
        }
    }

    &.show-patent-list {
        .patent-app__content {
            padding-right: $rightBarWidth;

            .patent-app__right-column {
                width: $rightBarWidth;
            }
        }
    }

    &.no-patent-list {
        .patent-app__content {
            padding-right: 0;
        }
    }

    .loading-box {
        width: 75px;
        height: 50px;
        pointer-events: none;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    //同族库模式下，function-bar需要左移且right-column需要上移
    &.family-search-mode {
        .patent-app__main {
            height: calc(100% - $familySearchHeight);
            .patent-app__right-column {
                height: calc(100% + $titleHeight) !important;
                top: calc(-#{$titleHeight}) !important;
            }
            .view-nav .view-nav__left-arrow {
                top: calc(150px + $familySearchHeight);
            }
        }
        .patent-app__main .patent-app__header {
            width: calc(100% - $rightBarCollapsedWidth);
        }
        &.show-patent-list {
            .patent-app__main .patent-app__header {
                width: calc(100% - $rightBarWidth);
            }
        }
        &.no-patent-list {
            .patent-app__main .patent-app__header {
                width: 100%;
            }
        }
    }
}

.kf5-content .zen-icon-attachment {
    color: #999;
    background-position: -80px -180px;
}

// fix bug PA-16803
.family-tree-full .patent-app .patent-app__main {
    margin-right: 0;
    margin-top: -130px;
}

.dual-view__container {
    position: relative;

    .view-right {
        flex: 1;
    }

    .dual-view__setting-button {
        position: absolute;
        right: 8px;
        top: 11px;
    }

    &--has-setting .view-right .ps-side-view__tab {
        padding-right: 40px;
    }
}
</style>
